/**
 * What a Venn diagram's regions hold, read from the boxes the reader filled in — in one
 * of three ways — and the set sizes behind the inclusion–exclusion working.
 *
 * - **Items**: each region's items typed straight into it, one per line.
 * - **Sets**: each set's members listed, then sorted into regions by set logic.
 * - **Counts**: one number per region.
 */

import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  REGION_IDS,
  REGION_SETS,
  type RegionId,
  type SetCount,
  type SetTotals,
  setTotals,
  sortIntoRegions,
  splitItems,
} from "@/lib/tool-kit/diagrams/venn";

export type VennMode = "items" | "sets" | "counts";

export const SET_LETTERS = ["A", "B", "C"] as const;

export interface VennInputs {
  regionText: Record<RegionId, string>;
  regionCounts: Record<RegionId, string>;
  /** Members of A, B and C (sets mode). */
  members: readonly string[];
  outside: { items: string; count: string; members: string };
  universe: boolean;
}

export interface VennContent {
  items: Record<RegionId, string[]>;
  /** Items per region, or the typed count; null for a blank count. */
  counts: Record<RegionId, number | null>;
  outsideItems: string[];
  /** Null without a universal set. */
  outsideCount: number | null;
  /** Set members missing from the universal set (sets mode). */
  notInUniverse: string[];
  /** Regions whose typed count was negative (and is ignored). */
  negative: RegionId[];
}

function noItems(): Record<RegionId, string[]> {
  return { a: [], b: [], c: [], ab: [], ac: [], bc: [], abc: [] };
}

function noCounts(): Record<RegionId, number | null> {
  return { a: null, b: null, c: null, ab: null, ac: null, bc: null, abc: null };
}

function countContent(
  ids: readonly RegionId[],
  input: VennInputs
): VennContent {
  const counts = noCounts();
  const negative: RegionId[] = [];
  for (const id of ids) {
    const value = parseNumberInput(input.regionCounts[id]);
    if (value !== null && value < 0) {
      negative.push(id);
    }
    counts[id] = value !== null && value >= 0 ? value : null;
  }
  const outside = parseNumberInput(input.outside.count);
  return {
    items: noItems(),
    counts,
    outsideItems: [],
    outsideCount:
      input.universe && outside !== null && outside >= 0 ? outside : null,
    notInUniverse: [],
    negative,
  };
}

/** What each region holds, read from whichever boxes the mode uses. */
export function readVennContent(
  mode: VennMode,
  setCount: SetCount,
  input: VennInputs
): VennContent {
  const ids = REGION_IDS[setCount];
  if (mode === "counts") {
    return countContent(ids, input);
  }
  let items = noItems();
  let outsideItems: string[] = [];
  let notInUniverse: string[] = [];
  if (mode === "sets") {
    const sorted = sortIntoRegions(
      input.members.slice(0, setCount).map((text) => splitItems(text, "list")),
      input.universe ? splitItems(input.outside.members, "list") : null
    );
    items = sorted.regions;
    outsideItems = sorted.outside;
    notInUniverse = sorted.notInUniverse;
  } else {
    for (const id of ids) {
      items[id] = splitItems(input.regionText[id], "lines");
    }
    if (input.universe) {
      outsideItems = splitItems(input.outside.items, "lines");
    }
  }
  const counts = noCounts();
  for (const id of ids) {
    counts[id] = items[id].length;
  }
  return {
    items,
    counts,
    outsideItems,
    outsideCount: input.universe ? outsideItems.length : null,
    notInUniverse,
    negative: [],
  };
}

/** The region in set notation: `A ∩ B′ ∩ C`. Outside every set: `(A ∪ B)′`. */
export function regionNotation(
  id: RegionId | "outside",
  setCount: SetCount
): string {
  const letters = SET_LETTERS.slice(0, setCount);
  if (id === "outside") {
    return `(${letters.join(" ∪ ")})′`;
  }
  return letters
    .map((letter, index) =>
      REGION_SETS[id].includes(index) ? letter : `${letter}′`
    )
    .join(" ∩ ");
}

/** Set sizes from the regions, blank counts taken as 0. */
export function vennTotals(
  setCount: SetCount,
  content: VennContent
): SetTotals {
  const numbers: Partial<Record<RegionId, number>> = {};
  for (const id of REGION_IDS[setCount]) {
    numbers[id] = content.counts[id] ?? 0;
  }
  return setTotals(setCount, numbers);
}

/* ------------------------------------------------------------------ words */

export interface RegionNameLabels {
  /** "{name} only" */
  regionOnly: string;
  /** The overlap of two sets: "Both". */
  regionBoth: string;
  /** "{first} and {second} only" */
  regionPair: string;
  /** "All three" */
  regionAll: string;
}

/** A region named after its sets: "Mitosis only", "Both", "Cats and Dogs only". */
export function regionName(
  id: RegionId,
  setCount: SetCount,
  names: readonly string[],
  labels: RegionNameLabels
): string {
  const sets = REGION_SETS[id];
  if (sets.length === 1) {
    return t(labels.regionOnly, { name: names[sets[0]] });
  }
  if (sets.length === 3) {
    return labels.regionAll;
  }
  return setCount === 2
    ? labels.regionBoth
    : t(labels.regionPair, { first: names[sets[0]], second: names[sets[1]] });
}

export interface WorkingLabels {
  /** "{letter} = {name}" */
  key: string;
  /** "n({letter}) = {count}" */
  setSize: string;
  /** "n({first} ∩ {second}) = {count}" */
  pairSize: string;
  unionTwo: string;
  unionThree: string;
  universeTotal: string;
}

type Format = (value: number) => string;

/** The inclusion–exclusion working, one line per fact. */
export function vennWorking(
  setCount: SetCount,
  content: VennContent,
  names: readonly string[],
  labels: WorkingLabels,
  fmt: Format
): string[] {
  const totals = vennTotals(setCount, content);
  const pairs: [string, string, number][] = [["A", "B", totals.pairs.ab]];
  if (setCount === 3) {
    pairs.push(["A", "C", totals.pairs.ac], ["B", "C", totals.pairs.bc]);
  }
  const [a = 0, b = 0, c = 0] = totals.sets;
  const values = {
    a: fmt(a),
    b: fmt(b),
    c: fmt(c),
    ab: fmt(totals.pairs.ab),
    ac: fmt(totals.pairs.ac),
    bc: fmt(totals.pairs.bc),
    abc: fmt(totals.all),
    union: fmt(totals.union),
  };
  const join = (parts: string[]) => parts.join(" · ");
  const lines = [
    join(
      names.map((name, i) => t(labels.key, { letter: SET_LETTERS[i], name }))
    ),
    join(
      totals.sets.map((count, i) =>
        t(labels.setSize, { letter: SET_LETTERS[i], count: fmt(count) })
      )
    ),
    join(
      pairs.map(([first, second, count]) =>
        t(labels.pairSize, { first, second, count: fmt(count) })
      )
    ),
    t(setCount === 2 ? labels.unionTwo : labels.unionThree, values),
  ];
  if (content.outsideCount !== null) {
    lines.push(
      t(labels.universeTotal, {
        union: values.union,
        outside: fmt(content.outsideCount),
        total: fmt(totals.union + content.outsideCount),
      })
    );
  }
  return lines;
}

/** The regions as table rows: name, notation, count and (unless counting) the items. */
export function vennRows(
  setCount: SetCount,
  content: VennContent,
  options: { counting: boolean; universe: boolean; outsideName: string },
  nameOf: (id: RegionId) => string,
  fmt: Format
): string[][] {
  const count = (value: number | null) => (value === null ? "" : fmt(value));
  const rows = REGION_IDS[setCount].map((id) => [
    nameOf(id),
    regionNotation(id, setCount),
    count(content.counts[id]),
    ...(options.counting ? [] : [content.items[id].join("; ")]),
  ]);
  if (options.universe) {
    rows.push([
      options.outsideName,
      regionNotation("outside", setCount),
      count(content.outsideCount),
      ...(options.counting ? [] : [content.outsideItems.join("; ")]),
    ]);
  }
  return rows;
}
